import { WHATS_NEW } from './announcements';
import { isAutoOpenActive, WhatsNewAnnouncement } from './whats-new.types';

/** Where the seen state lives. Shape: `{ [account]: announcementId[] }`. */
export const STORAGE_KEY = 'whats_new_seen';

/** Bucket used while nobody is signed in, or when the email is unknown. */
export const ANONYMOUS_ACCOUNT = 'anonymous';

export type SeenState = Record<string, string[]>;

/**
 * Normalise an account key.
 *
 * Keyed by email because it is the only stable identifier the console has:
 * `User.id` carries the Fusion session id, which is a different value on every
 * login and would re-announce the same feature forever.
 */
export function accountKey(email: string | null | undefined): string {
  return email?.trim().toLowerCase() || ANONYMOUS_ACCOUNT;
}

/**
 * Read the seen state, tolerating anything that is not the shape we wrote.
 *
 * Storage is shared with the user and with older builds, so a broken value must
 * never take the console down — it is treated as "nothing seen".
 */
export function readSeenState(storage: Storage): SeenState {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const state: SeenState = {};
    for (const [account, ids] of Object.entries(parsed as SeenState)) {
      if (Array.isArray(ids)) {
        state[account] = ids.filter(
          (id): id is string => typeof id === 'string',
        );
      }
    }
    return state;
  } catch {
    return {};
  }
}

/**
 * Record an announcement as seen. Returns the written state.
 *
 * Failures are swallowed: in private mode or with a full quota the announcement
 * simply shows again, which beats breaking the page over a dismissal.
 */
export function writeSeen(
  storage: Storage,
  account: string,
  id: string,
): SeenState {
  const state = readSeenState(storage);
  const seen = new Set(state[account] ?? []);
  seen.add(id);
  state[account] = [...seen];
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Nothing to do — see above.
  }
  return state;
}

/**
 * The newest announcement this account has not seen, or `null`.
 *
 * Ids that are no longer in {@link WHATS_NEW} stay in storage untouched: a
 * rollback would otherwise re-announce a feature everyone already knows.
 *
 * `skipIds` covers what was already shown in this session but deliberately not
 * stored — somebody who unticks "Don't show this again" must not see it twice
 * in a row, but must not block the queue for everything behind it either.
 */
export function pendingAnnouncement(
  seen: SeenState,
  account: string,
  announcements: WhatsNewAnnouncement[] = WHATS_NEW,
  now: Date = new Date(),
  skipIds: ReadonlySet<string> = new Set(),
): WhatsNewAnnouncement | null {
  const seenIds = new Set(seen[account] ?? []);
  const unseen = announcements.filter(
    (a) => !seenIds.has(a.id) && !skipIds.has(a.id) && isAutoOpenActive(a, now),
  );
  if (!unseen.length) return null;

  return unseen.reduce((newest, candidate) =>
    candidate.publishedAt > newest.publishedAt ? candidate : newest,
  );
}

/** What a `?whatsnew=…` parameter asks the console to do. */
export type WhatsNewTrigger =
  | { kind: 'none' }
  /** Show it the way a first-time visitor would see it. */
  | { kind: 'reset' }
  /** Force open, whatever the stored state and the expiry date say. */
  | { kind: 'open'; announcement: WhatsNewAnnouncement };

/**
 * Read the `?whatsnew=…` escape hatch.
 *
 * Announcements are by design hard to see twice — which makes them equally hard
 * to review. This parameter reopens one on demand:
 *
 * - `?whatsnew=1` (or `latest`, `true`) — the newest announcement,
 * - `?whatsnew=<id>` — a specific one,
 * - `?whatsnew=reset` — forget what this account has seen and start over.
 *
 * It ships in every build on purpose: the content is public either way, and a
 * link that opens the feature intro is useful in support and in release notes.
 */
export function resolveQueryTrigger(
  value: string | null | undefined,
  announcements: WhatsNewAnnouncement[] = WHATS_NEW,
): WhatsNewTrigger {
  const wanted = value?.trim().toLowerCase();
  if (!wanted) return { kind: 'none' };
  if (wanted === 'reset') return { kind: 'reset' };

  if (['1', 'true', 'latest'].includes(wanted)) {
    const newest = [...announcements].sort((a, b) =>
      b.publishedAt.localeCompare(a.publishedAt),
    )[0];
    return newest ? { kind: 'open', announcement: newest } : { kind: 'none' };
  }

  const match = announcements.find((a) => a.id.toLowerCase() === wanted);
  return match ? { kind: 'open', announcement: match } : { kind: 'none' };
}

/** Drop everything this account has seen, so the next check starts fresh. */
export function forgetAccount(storage: Storage, account: string): void {
  const state = readSeenState(storage);
  delete state[account];
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Nothing to do — the reset simply does not stick.
  }
}
